import React from 'react';
import { Plus, Trash2, ArrowLeftRight } from 'lucide-react';

const SheetsGrid = ({
  items,
  onChange,
  showPrice = true,
  showLocation = true,
  showObservation = true,
  showRegisterDate = true,
  enableToggle = true,
  emptyMessage = 'Nenhuma chapa adicionada'
}) => {
  const handleFieldChange = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleAdd = () => {
    const today = new Date().toISOString().split('T')[0];
    onChange([
      ...items,
      {
        tempId: Date.now(),
        quantity: 1,
        length: '',
        width: '',
        price: '',
        observation: '',
        location: '',
        isEnabled: true,
        registeredDate: today,
        isReserved: false,
        reserveDate: null
      }
    ]);
  };

  const handleRemove = (index) => {
    const updated = items.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleSwapDimensions = (index) => {
    const updated = [...items];
    const item = updated[index];
    updated[index] = {
      ...item,
      length: item.width,
      width: item.length
    };
    onChange(updated);
  };

  const swapAll = () => {
    const updated = items.map(item => ({
      ...item,
      length: item.width,
      width: item.length
    }));
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {items.length} {items.length === 1 ? 'item' : 'itens'} • Marque "Hab" para incluir no cálculo
        </p>
        <div className="flex gap-2">
          {items.length > 0 && (
            <button
              type="button"
              onClick={swapAll}
              className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-1"
              title="Inverter todas as dimensões"
            >
              <ArrowLeftRight className="w-3 h-3" />
              Inverter todas
            </button>
          )}
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-blue-600 text-white">
            <tr>
              <th className="px-2 py-2 text-center w-8">N</th>
              {enableToggle && <th className="px-2 py-2 text-center w-12">Hab</th>}
              <th className="px-2 py-2 text-center w-16">Qtd</th>
              <th className="px-2 py-2 text-left">Comp. (mm)</th>
              <th className="px-2 py-2 text-center w-8">⇄</th>
              <th className="px-2 py-2 text-left">Larg. (mm)</th>
              {showPrice && <th className="px-2 py-2 text-right w-20">Preço</th>}
              {showObservation && <th className="px-2 py-2 text-left">Observação</th>}
              {showLocation && <th className="px-2 py-2 text-left w-24">Local</th>}
              {showRegisterDate && <th className="px-2 py-2 text-center w-28">Registro</th>}
              <th className="px-2 py-2 text-center w-10">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-6 text-center text-gray-400 text-xs">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              items.map((item, idx) => (
                <tr key={item.id || item.tempId || idx} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-2 py-1.5 text-center font-medium text-gray-500">{idx + 1}</td>
                  {enableToggle && (
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={item.isEnabled !== false}
                        onChange={(e) => handleFieldChange(idx, 'isEnabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      min="0"
                      value={item.quantity ?? ''}
                      onChange={(e) => handleFieldChange(idx, 'quantity', e.target.value)}
                      className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs text-center"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      min="5"
                      max="99000"
                      value={item.length ?? ''}
                      onChange={(e) => handleFieldChange(idx, 'length', e.target.value)}
                      className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs"
                      placeholder="Comp."
                    />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <button
                      type="button"
                      onClick={() => handleSwapDimensions(idx)}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Inverter comp/larg"
                    >
                      <ArrowLeftRight className="w-3.5 h-3.5" />
                    </button>
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      min="5"
                      max="99000"
                      value={item.width ?? ''}
                      onChange={(e) => handleFieldChange(idx, 'width', e.target.value)}
                      className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs"
                      placeholder="Larg."
                    />
                  </td>
                  {showPrice && (
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.price ?? ''}
                        onChange={(e) => handleFieldChange(idx, 'price', e.target.value)}
                        className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs text-right"
                        placeholder="0.00"
                      />
                    </td>
                  )}
                  {showObservation && (
                    <td className="px-2 py-1.5">
                      <input
                        type="text"
                        maxLength={showRegisterDate ? 100 : 60}
                        value={item.observation ?? ''}
                        onChange={(e) => handleFieldChange(idx, 'observation', e.target.value)}
                        className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs"
                      />
                    </td>
                  )}
                  {showLocation && (
                    <td className="px-2 py-1.5">
                      <input
                        type="text"
                        maxLength={40}
                        value={item.location ?? ''}
                        onChange={(e) => handleFieldChange(idx, 'location', e.target.value)}
                        className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs"
                      />
                    </td>
                  )}
                  {showRegisterDate && (
                    <td className="px-2 py-1.5">
                      <input
                        type="date"
                        value={item.registeredDate || ''}
                        onChange={(e) => handleFieldChange(idx, 'registeredDate', e.target.value)}
                        className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs"
                      />
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-center">
                    <button
                      type="button"
                      onClick={() => handleRemove(idx)}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Remover"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={handleAdd}
        className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Adicionar linha
      </button>
    </div>
  );
};

export default SheetsGrid;